import {
  PROCESS_DEVICE_PREFIX,
  SYSTEM_DEFAULT_DEVICE_MARKER,
  SYSTEM_WIDE_DEVICE_MARKER,
} from '@/tauri/constants';
import { logger } from '@/utils/logger/logger';
import { isWindows } from '@heidi/utils';
import { type AudioDevice } from 'src-tauri/src/bindings/AudioDevice';

/**
 * Speaker capture mode implied by the resolved output device name. Locked
 * into `tauriRecordModel` at recording start so restart flows, analytics
 * enrichment and the silent-capture warning layer all read the same decision.
 */
export type TauriSpeakerCaptureMode =
  | 'system_wide'
  | 'system_default'
  | 'process'
  | 'device';

export type ResolvedStartDeviceNames = {
  inputDeviceName: string;
  outputDeviceName: string;
  speakerCaptureMode: TauriSpeakerCaptureMode;
};

const speakerCaptureModeFor = (outputDeviceName: string): TauriSpeakerCaptureMode => {
  if (outputDeviceName === SYSTEM_WIDE_DEVICE_MARKER) return 'system_wide';
  if (outputDeviceName === SYSTEM_DEFAULT_DEVICE_MARKER) return 'system_default';
  if (outputDeviceName.startsWith(PROCESS_DEVICE_PREFIX)) return 'process';
  return 'device';
};

/**
 * Shared start-time device-name resolution, applied at BOTH recording start
 * sites (`useMutateStartTauriRecording` and the legacy restart in
 * `useMutateTauriChangeAudioInput`'s `onSettled`) so a mid-recording restart
 * re-derives exactly what a fresh start would.
 *
 * Speaker rule — flag ON ∧ Windows ∧ selection is the "System audio" entry or
 * `__SYSTEM_DEFAULT__` → `__SYSTEM_WIDE__` (whole-system capture). Explicit
 * physical devices keep device loopback and explicit app entries keep
 * per-process INCLUDE capture. With the flag OFF (kill switch) or
 * off-Windows, a persisted "System audio" selection degrades to
 * `__SYSTEM_DEFAULT__` so the sentinel can never reach Rust while the
 * feature is disarmed.
 *
 * Mic rule — flag ON ∧ Windows ∧ selection is the `__SYSTEM_DEFAULT__`
 * sentinel ∧ a communications-default input exists that differs from the
 * console default → that device's concrete name (the headset mic calling
 * apps record from). Explicit selections always pass through, and callers
 * must never persist the resolved name over the sentinel. When the mic
 * params are omitted the mic name passes through untouched — the legacy
 * mid-recording restart omits them deliberately (its device list can be
 * stale during device-loss recovery).
 */
export function resolveDeviceNamesForStart({
  inputDeviceName,
  outputDeviceName,
  isSystemAudioCaptureEnabled,
  inputDevices,
  isCommsDefaultMicEnabled = false,
}: {
  inputDeviceName: string;
  outputDeviceName: string;
  /** `useIsAppsWindowsSystemAudioCaptureDesktopEnabled()`, read at the call site's hook top. */
  isSystemAudioCaptureEnabled: boolean;
  /** Fresh device list for the mic rule; omit to pass the mic through untouched. */
  inputDevices?: AudioDevice[];
  /** `useIsAppsWindowsCommsDefaultMicDesktopEnabled()`, read at the call site's hook top. */
  isCommsDefaultMicEnabled?: boolean;
}): ResolvedStartDeviceNames {
  const systemWideEligible = isSystemAudioCaptureEnabled && isWindows;
  const selectionIsSystemSentinel =
    outputDeviceName === SYSTEM_DEFAULT_DEVICE_MARKER ||
    outputDeviceName === SYSTEM_WIDE_DEVICE_MARKER;

  let resolvedOutputDeviceName = outputDeviceName;
  if (systemWideEligible && selectionIsSystemSentinel) {
    resolvedOutputDeviceName = SYSTEM_WIDE_DEVICE_MARKER;
  } else if (outputDeviceName === SYSTEM_WIDE_DEVICE_MARKER) {
    resolvedOutputDeviceName = SYSTEM_DEFAULT_DEVICE_MARKER;
  }

  return {
    inputDeviceName: resolveStartInputDeviceName({
      inputDeviceName,
      inputDevices,
      isCommsDefaultMicEnabled,
    }),
    outputDeviceName: resolvedOutputDeviceName,
    speakerCaptureMode: speakerCaptureModeFor(resolvedOutputDeviceName),
  };
}

/**
 * Mic half of the start-time resolution (see `resolveDeviceNamesForStart`
 * docs for the rule). Exported so pre-start health probes can target the
 * same endpoint a real start would record from.
 */
export const resolveStartInputDeviceName = ({
  inputDeviceName,
  inputDevices,
  isCommsDefaultMicEnabled,
  silent = false,
}: {
  inputDeviceName: string;
  inputDevices?: AudioDevice[];
  isCommsDefaultMicEnabled: boolean;
  silent?: boolean;
}): string => {
  if (!isCommsDefaultMicEnabled || !isWindows || !inputDevices) {
    return inputDeviceName;
  }
  // Explicit selections are never touched.
  if (inputDeviceName !== SYSTEM_DEFAULT_DEVICE_MARKER) {
    return inputDeviceName;
  }

  const commsDefault = inputDevices.find((d) => d.is_communications_default);
  if (!commsDefault) {
    return inputDeviceName;
  }
  // Comms default == console default: keep the sentinel so Rust resolves the
  // same device and the selection keeps tracking future OS-default changes.
  if (commsDefault.is_default) {
    return inputDeviceName;
  }

  // Device name only at debug — raw names can carry user-personalized
  // strings. The Rust start log carries the concrete device for support.
  if (!silent) {
    logger.info(
      '[ResolveDeviceNamesForStart] Resolving __SYSTEM_DEFAULT__ mic to the communications-default device'
    );
    logger.debug('[ResolveDeviceNamesForStart] Communications-default mic', {
      device: commsDefault.name,
    });
  }
  return commsDefault.name;
};
